import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { StoreConfig } from '../services/backend';

interface FloatingCartBarProps {
  quotationMode?: StoreConfig['quotationMode'];
}

const FloatingCartBar: React.FC<FloatingCartBarProps> = ({ quotationMode }) => {
  const { cart, cartCount, cartTotal } = useCart();
  const location = useLocation();

  const shouldHideOnRoute =
    location.pathname === '/carrinho' || location.pathname.startsWith('/checkout');

  if (cart.length === 0 || shouldHideOnRoute) {
    return null;
  }

  const formatMoney = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const previewItems = cart.slice(0, 3);
  const extraItemsCount = Math.max(cart.length - previewItems.length, 0);

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] border-t border-[#ffd5e6] bg-white/95 shadow-[0_-18px_45px_rgba(20,20,20,0.12)] backdrop-blur-md">
      <div
        className="mx-auto flex max-w-6xl items-center gap-3 px-3 py-3 sm:px-4"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="hidden shrink-0 items-center -space-x-3 sm:flex">
            {previewItems.map((item) => {
              const imageUrl =
                Array.isArray(item.images) && item.images.length > 0
                  ? typeof item.images[0] === 'string'
                    ? item.images[0]
                    : item.images[0]?.url
                  : item.image;

              return (
                <div
                  key={item.id}
                  className="h-12 w-12 overflow-hidden rounded-full border-2 border-white bg-[#fff4f9] shadow-sm"
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt={item.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-veraz-pink">
                      <ShoppingBag size={16} />
                    </div>
                  )}
                </div>
              );
            })}

            {extraItemsCount > 0 && (
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-[#ffe5f1] text-[11px] font-bold text-veraz-pink shadow-sm">
                +{extraItemsCount}
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-veraz-pink">
              seu carrinho
            </p>
            <p className="truncate text-sm font-semibold text-[#181818] sm:text-[15px]">
              {cartCount} item(ns) adicionado(s)
            </p>
            <p className="text-xs text-gray-500 sm:text-sm">
              {previewItems.map((item) => item.name).join(' | ')}
              {extraItemsCount > 0 ? ' | etc' : ''}
            </p>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-gray-500">
            {quotationMode ? 'itens' : 'subtotal'}
          </p>
          <p className="text-sm font-bold text-[#181818] sm:text-lg">
            {quotationMode ? `${cartCount} item(ns)` : formatMoney(cartTotal)}
          </p>
        </div>

        <Link
          to="/carrinho"
          className="inline-flex shrink-0 items-center justify-center rounded-full bg-veraz-pink px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#e0007a] sm:px-5 sm:text-xs"
        >
          ir pro carrinho
        </Link>
      </div>
    </div>
  );
};

export default FloatingCartBar;
